import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { MessageService } from '@services/helpers/message-service/message.service';
import { PaginationService } from '@services/pagination/pagination.service';
import { ProductPackagesDataServiceProvider } from '@services/settings/product-packages.data.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';
import { UpdatePackageProductModalComponent } from '@web-components/packages-product/modals/update-package-product-modal/update-package-product-modal.component';
import { Observable } from 'rxjs/Observable';
import { ProductPackageService } from '@web-services/settings/productPackage.service';
import { Subscription } from 'rxjs/Subscription';
import {
	IItemsOverviewGen,
	IModalAction,
	IProductPackageItem,
	PageRequest
} from '@models/models';

@Component({
	selector: 'packages-block',
	template: require('./packages-block.component.html')
})
export class PackagesBlockComponent implements OnInit, OnDestroy {
	@Input() companyId: number;
	modalTitle: string;
	pageRequest: PageRequest;
	packages: IProductPackageItem[] = [];
	itemsQuantity: number;
	pageNumbers: Array<number>;
	private allowedFilters: string[];

	readonly MODAL_TITLES: Array<string> = [
		'Add New Package',
		'Duplicate Package',
		'Edit Package'
	];

	readonly PACKAGE_REQUEST_DEFAULT: PageRequest = {
		filterBy: 'packages',
		paging: {
			pageNumber: 0,
			pageSize: 10,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	readonly PACKAGE_DEFAULT: IProductPackageItem = {
		name: '',
		taxType: 'INCLUDED',
		type: 'PACKAGE',
		description: ''
	};

	modalDataSubscription: Subscription;
	selectedPackageModalRef: NgbModalRef;

	constructor(
		private productPackagesDataService: ProductPackagesDataServiceProvider,
		private productPackageService: ProductPackageService,
		private messageService: MessageService,
		private paginationService: PaginationService,
		private modalService: SNModalService
	) {}

	ngOnInit(): void {
		this.applyPagePackageFromLocalStorage();
		this.getPackagesData();
		this.productPackagesDataService.updateProductPackageSubject
			.debounceTime(500)
			.subscribe((): void => {
				this.pageRequest.paging.searchToken = null;
				this.pageRequest.paging.pageNumber = 0;
				this.getPackagesData();
			});
	}

	ngOnDestroy(): void {
		this.productPackagesDataService.updateProductPackageSubject.complete();
	}

	isFilterAllowed(filterName: string): boolean {
		if (!this.allowedFilters) {
			return false;
		}
		return this.allowedFilters.includes(filterName);
	}

	orderByField(filter: string): void {
		if (this.pageRequest.sortCriterias[0].fieldName === filter) {
			this.pageRequest.sortCriterias[0].sortAscending = !this.pageRequest
				.sortCriterias[0].sortAscending;
		} else {
			this.pageRequest.sortCriterias[0].fieldName = filter;
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}
		this.pageRequest.paging.pageNumber = 0;

		this.getPackagesData();
	}

	openEditPackageModal(packageItem: IProductPackageItem): void {
		this.selectedPackageModalRef = this.modalService.openModal(
			UpdatePackageProductModalComponent
		);

		this.subscribeToModalData();

		this.selectedPackageModalRef.componentInstance.modalTitle = this.MODAL_TITLES[2];
		this.selectedPackageModalRef.componentInstance.name = 'Package';
		this.selectedPackageModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(packageItem)
		);
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
	}

	openDuplicatePackageModal(packageItem: IProductPackageItem): void {
		this.selectedPackageModalRef = this.modalService.openModal(
			UpdatePackageProductModalComponent
		);

		this.subscribeToModalData();

		this.selectedPackageModalRef.componentInstance.modalTitle = this.MODAL_TITLES[1];
		this.selectedPackageModalRef.componentInstance.name = 'Package';
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
		this.selectedPackageModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(packageItem)
		);
	}

	openAddNewPackageModal(): void {
		this.selectedPackageModalRef = this.modalService.openModal(
			UpdatePackageProductModalComponent
		);

		this.subscribeToModalData();

		this.selectedPackageModalRef.componentInstance.modalTitle = this.MODAL_TITLES[0];
		this.selectedPackageModalRef.componentInstance.name = 'Package';
		this.selectedPackageModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(this.PACKAGE_DEFAULT)
		);
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
	}

	openDeletePackageModal(packageItem: IProductPackageItem): void {
		this.selectedPackageModalRef = this.modalService.openModal(
			DeleteItemModalComponent
		);

		this.subscribeToModalData();

		this.selectedPackageModalRef.componentInstance.modalTitle = 'Package';
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
		this.selectedPackageModalRef.componentInstance.modalData = JSON.parse(
			JSON.stringify(packageItem)
		);
		this.selectedPackageModalRef.componentInstance.itemName = packageItem.name;
	}

	convertPackageToProduct(packageItem: IProductPackageItem): void {
		const body = {
			productTemplateId: packageItem.id,
			requiredProductType: 'ADD_ON'
		};
		this.productPackagesDataService
			.convertProductPackage(body)
			.subscribe((): void => {
				this.productPackagesDataService.updateProductPackageSubject.next(
					'updateProducts'
				);
				this.messageService.showToaster(
					's_10',
					'productsPackages',
					packageItem.name
				);
			});
	}

	updatePackageData(pageRequest: PageRequest): void {
		this.pageRequest = pageRequest;
		this.getPackagesData();
	}

	private subscribeToModalData(): void {
		this.selectedPackageModalRef.result.then(
			(): void => this.modalDataSubscription.unsubscribe(),
			(): void => this.modalDataSubscription.unsubscribe()
		);

		this.modalDataSubscription = this.modalService
			.getModalData()
			.filter(data => data && data.action && data.props)
			.subscribe((data: IModalAction<IProductPackageItem>) =>
				this.packageModalActions(data)
			);
	}

	private getPackagesData(): void {
		this.productPackagesDataService
			.getPackagesOrProducts(this.pageRequest)
			.subscribe((data: IItemsOverviewGen<IProductPackageItem>) =>
				this.successGetPackageData(data)
			);
	}

	private successGetPackageData(
		data: IItemsOverviewGen<IProductPackageItem>
	): void {
		this.packages = JSON.parse(JSON.stringify(data.items));
		this.itemsQuantity = data ? data.totalSize : 0;
		this.pageNumbers = this.paginationService.calculatePageNumbers(
			this.itemsQuantity,
			this.pageRequest.paging.pageSize
		);
		this.pageRequest.paging.searchToken = data.searchToken;
		this.allowedFilters = data.sortingFields;
		this.setPagePackageToLocalStorage(this.pageRequest);
	}

	private packageModalActions({
		action,
		props
	}: IModalAction<IProductPackageItem>): void {
		this.selectedPackageModalRef.componentInstance.btnDisabled = true;

		switch (action) {
			case 'Add New Package':
				this.addNewPackage(props).subscribe(
					(data: IProductPackageItem): void => {
						this.messageService.showToaster(
							's_0',
							'productsPackages',
							data.name
						);
					}
				);
				break;
			case 'Duplicate Package':
				this.addNewPackage(props).subscribe(
					(data: IProductPackageItem): void => {
						this.messageService.showToaster(
							's_2',
							'productsPackages',
							data.name
						);
					}
				);
				break;
			case 'Edit Package':
				this.updatePackage(props).subscribe(
					(data: IProductPackageItem): void => {
						this.messageService.showToaster(
							's_3',
							'productsPackages',
							data.name
						);
					}
				);
				break;
			case 'Delete':
				this.deletePackage(props).subscribe((): void => {
					this.messageService.showToaster(
						's_1',
						'productsPackages',
						props.name
					);
				});
				break;
			default:
				break;
		}
	}

	private addNewPackage(
		dataToSend: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesDataService
			.createPackagesOrProducts(dataToSend)
			.map((data: IProductPackageItem) =>
				this.resetPackageDataAfterAction(data)
			);
	}

	private updatePackage(
		dataToSend: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesDataService
			.updatePackagesOrProducts(dataToSend.id, dataToSend)
			.map((data: IProductPackageItem) =>
				this.resetPackageDataAfterAction(data)
			);
	}

	private deletePackage(
		props: IProductPackageItem
	): Observable<IProductPackageItem> {
		return this.productPackagesDataService
			.deletePackagesOrProducts(props.id)
			.map((data: IProductPackageItem) =>
				this.resetPackageDataAfterAction(data)
			);
	}

	private resetPackageDataAfterAction(
		data: IProductPackageItem
	): IProductPackageItem {
		this.pageRequest.paging.searchToken = null;
		this.pageRequest.paging.pageNumber = 0;
		this.getPackagesData();
		this.modalDataSubscription.unsubscribe();
		this.selectedPackageModalRef.componentInstance.btnDisabled = false;
		this.selectedPackageModalRef.close();
		return data;
	}

	// LOCAL STORAGE
	private applyPagePackageFromLocalStorage(): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`settingsPackageRequest_${this.companyId}`)
		);
		this.pageRequest = this.PACKAGE_REQUEST_DEFAULT;
		if (
			pageRequestObject !== null &&
			pageRequestObject !== undefined &&
			pageRequestObject !== ''
		) {
			const { pageSize, fieldName, sortAscending } = pageRequestObject;
			this.pageRequest.paging.pageSize = pageSize;
			this.pageRequest.sortCriterias[0].fieldName = fieldName;
			this.pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private setPagePackageToLocalStorage(pageRequest): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`settingsPackageRequest_${this.companyId}`);
		localStorage.setItem(
			`settingsPackageRequest_${this.companyId}`,
			pageRequestString
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/packages-product/packages-block/packages-block.component.ts